// ABOUTME: Displays top 10 slowest-selling products (shelfwarmers) for a relation.
// ABOUTME: Uses the same visual style as the relation dashboard product statistics.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import { useGetRelationDashboardProductsForRelationQuery } from "../../../../../newapi/warehouse/productSlice";
import Text from "../../../../../components/theme/text/Text";
import Subheading from "../../../../../components/theme/text/Subheading";
import ShRowSkeleton from "../../../../../components/theme/skeleton/ShRowSkeleton";
import ShDateRangePicker from "../../../../../components/theme/ShDateRangePicker";

const getColorForIndex = (index, theme, colors) => {
    if (index === 0) {
        return {
            iconBg: "#D7AC0E",
            iconColor: "#fff",
            background: "#FBF7E7",
            color: "#31415f",
            countColor: "#A7860B",
            countWeight: 1000,
        };
    } else if (index === 1) {
        return {
            iconBg: "#8590A2",
            iconColor: "#fff",
            background: "#F3F4F6",
            color: "#31415f",
            countColor: "#31415F",
            countWeight: 1000,
        };
    } else if (index === 2) {
        return {
            iconBg: "#D7840E",
            iconColor: "#fff",
            background: "#FBF3E7",
            color: "#31415f",
            countColor: "#D7840E",
            countWeight: 1000,
        };
    } else {
        return {
            iconBg: theme.palette.mode === "dark" ? colors.grey[800] : "#FFF",
            iconColor: theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
            background: theme.palette.mode === "dark" ? colors.grey[400] : "#FFF",
            color: theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
            countColor: theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
            countWeight: 400,
        };
    }
};

const ProductRow = ({ product, index, colorMap }) => {
    return (
        <Box
            sx={{
                mt: index > 0 ? 1 : 2,
                height: 32,
                background: colorMap.background,
                borderRadius: 5,
                pl: 0.5,
                pr: 2,
                display: "flex",
                alignItems: "center",
                fontFamily: "Roboto, sans-serif",
                overflow: "hidden",
                width: 1,
            }}
        >
            <Box
                sx={{
                    height: 24,
                    width: 24,
                    flexShrink: 0,
                    background: colorMap.iconBg,
                    textAlign: "center",
                    lineHeight: "24px",
                    fontSize: 17,
                    fontWeight: 500,
                    borderRadius: 4,
                    color: colorMap.iconColor,
                }}
            >
                {index + 1}
            </Box>
            <Box
                sx={{
                    textAlign: "left",
                    px: 1.5,
                    flex: 1,
                    fontSize: 14,
                    wordBreak: "keep-all",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: colorMap.color,
                }}
            >
                {product.description}
            </Box>
            <Box
                sx={{
                    fontWeight: colorMap.countWeight,
                    color: colorMap.countColor,
                    fontSize: 17,
                }}
            >
                {product.total_amount}
            </Box>
        </Box>
    );
};

const RelationShelfwarmers = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const getDefaultDates = () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        return {
            startDate: start.toISOString().split("T")[0],
            endDate: end.toISOString().split("T")[0],
        };
    };

    const [dateRange, setDateRange] = useState(getDefaultDates);

    const handleDateChange = (value) => {
        setDateRange({
            startDate: value[0].formattedStartDate,
            endDate: value[0].formattedEndDate,
        });
    };

    const { data: dashboardData, isLoading, isFetching } = useGetRelationDashboardProductsForRelationQuery(
        { relationId: props.relation, startDate: dateRange.startDate, endDate: dateRange.endDate },
        { skip: !props.relation }
    );

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });

        return () => {};
    }, [props.relation]);

    const isDataLoading = isLoading || isFetching;

    return (
        <Box
            height={1}
            display="flex"
            flexDirection="column"
            sx={{
                p: 3,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    flexShrink: 0,
                    p: 3,
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                    border: `1px solid ${colors.grey[100]}`,
                    boxShadow: `0px 2px 4px ${colors.grey[200]}`,
                    borderRadius: 4,
                    overflow: "hidden",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Subheading>{t("top_10_slowestsellers")}</Subheading>
                    <Box sx={{ flexShrink: 0 }}>
                        <ShDateRangePicker
                            select={handleDateChange}
                            explicit
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            size={32}
                        />
                    </Box>
                </Box>

                <Box>
                    {isDataLoading ? (
                        Array.from({ length: 10 }).map((_, index) => (
                            <Box key={index} sx={{ mt: index > 0 ? 1 : 2 }}>
                                <ShRowSkeleton />
                            </Box>
                        ))
                    ) : dashboardData?.slowestSellers?.length > 0 ? (
                        dashboardData.slowestSellers.map((product, index) => (
                            <ProductRow
                                key={"shelfwarmer" + index}
                                index={index}
                                colorMap={getColorForIndex(index, theme, colors)}
                                product={product}
                            />
                        ))
                    ) : (
                        <Text light sx={{ mt: 2 }}>{t("no_data_available")}</Text>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default RelationShelfwarmers;
